import React, { useMemo, useState, memo, useCallback } from "react";
import { Spin } from "antd";
import { PerformanceTracker } from "./performance-tracker";
import { useAppSelector } from "@/store/hooks";
import {
  selectApexChartData,
  selectApexLoading,
} from "@/store/features/apex/apex-selectors";
import {
  selectPlaidChartData,
  selectPlaidLoading,
} from "@/store/features/plaid/plaid-selectors";
import { TimeRange } from "@/api/custom";

interface ChartTabProps {
  onDataUpdate?: (data: {
    selectedTotal: number | null;
    lastTotal: number;
    delta: { value: number; percentage: number };
  }) => void;
  timeSelection?: TimeRange;
  onTimeSelectionChange?: (value: TimeRange) => void;
}

const ChartTab: React.FC<ChartTabProps> = ({
  onDataUpdate,
  timeSelection: timeSelectionProp,
  onTimeSelectionChange,
}) => {
  const [localTimeSelection, setLocalTimeSelection] = useState<TimeRange>("ALL");
  const timeSelection = timeSelectionProp ?? localTimeSelection;

  const handleTimeSelectionChange = useCallback(
    (newTimeSelection: TimeRange) => {
      if (onTimeSelectionChange) {
        onTimeSelectionChange(newTimeSelection);
      } else {
        setLocalTimeSelection(newTimeSelection);
      }
    },
    [onTimeSelectionChange],
  );

  const apexChartData = useAppSelector(selectApexChartData);
  const plaidChartData = useAppSelector(selectPlaidChartData);
  const apexLoading = useAppSelector(selectApexLoading);
  const plaidLoading = useAppSelector(selectPlaidLoading);

  const chartData = useMemo(() => {
    const apexHasData = apexChartData.timestamps.length > 0;
    const plaidHasData = plaidChartData.timestamps.length > 0;

    if (!apexHasData && !plaidHasData) {
      return { timestamps: [], values: [] };
    }

    const apexByTimestamp = new Map<number, number | null>();
    const plaidByTimestamp = new Map<number, number | null>();

    const apexPairCount = Math.min(
      apexChartData.timestamps.length,
      apexChartData.values.length,
    );
    for (let index = 0; index < apexPairCount; index += 1) {
      const timestamp = apexChartData.timestamps[index];
      const parsedValue = parseFloat(String(apexChartData.values[index]));
      apexByTimestamp.set(
        timestamp,
        Number.isFinite(parsedValue) ? parsedValue : null,
      );
    }

    const plaidPairCount = Math.min(
      plaidChartData.timestamps.length,
      plaidChartData.values.length,
    );
    for (let index = 0; index < plaidPairCount; index += 1) {
      const timestamp = plaidChartData.timestamps[index];
      const parsedValue = parseFloat(String(plaidChartData.values[index]));
      plaidByTimestamp.set(
        timestamp,
        Number.isFinite(parsedValue) ? parsedValue : null,
      );
    }

    // Merge by timestamp using carry-forward so one stale source does not
    // truncate the combined total series.
    const timestamps = Array.from(
      new Set(
        [
          ...Array.from(apexByTimestamp.keys()),
          ...Array.from(plaidByTimestamp.keys()),
        ]
          .map((timestamp) => Number(timestamp))
          .filter((timestamp) => Number.isFinite(timestamp)),
      ),
    ).sort((a, b) => a - b);

    let latestApexValue: number | null = null;
    let latestPlaidValue: number | null = null;
    const values = timestamps.map((timestamp) => {
      const apexValueForTimestamp = apexByTimestamp.get(timestamp);
      if (
        apexByTimestamp.has(timestamp) &&
        apexValueForTimestamp !== null &&
        apexValueForTimestamp !== undefined
      ) {
        latestApexValue = apexValueForTimestamp;
      }
      const plaidValueForTimestamp = plaidByTimestamp.get(timestamp);
      if (
        plaidByTimestamp.has(timestamp) &&
        plaidValueForTimestamp !== null &&
        plaidValueForTimestamp !== undefined
      ) {
        latestPlaidValue = plaidValueForTimestamp;
      }

      const apexValue = latestApexValue ?? 0;
      const plaidValue = latestPlaidValue ?? 0;
      return String(apexValue + plaidValue);
    });

    return { timestamps, values };
  }, [
    apexChartData.timestamps,
    apexChartData.values,
    plaidChartData.timestamps,
    plaidChartData.values,
  ]);

  const loadingStates = useMemo(() => {
    const apexDataLoading = apexLoading.chart;
    const plaidDataLoading = plaidLoading.chart;
    const isLoading = apexDataLoading || plaidDataLoading;

    const apexDataAvailable =
      apexChartData.timestamps.length > 0 || apexChartData.values.length > 0;
    const plaidDataAvailable =
      plaidChartData.timestamps.length > 0 || plaidChartData.values.length > 0;

    const bothDataSourcesReady = !apexDataLoading && !plaidDataLoading;

    return {
      isLoading,
      apexDataLoading,
      plaidDataLoading,
      apexDataAvailable,
      plaidDataAvailable,
      bothDataSourcesReady,
      hasAnyData: apexDataAvailable || plaidDataAvailable,
    };
  }, [
    apexLoading.chart,
    plaidLoading.chart,
    apexChartData.timestamps.length,
    apexChartData.values.length,
    plaidChartData.timestamps.length,
    plaidChartData.values.length,
  ]);

  const getLoadingMessage = () => {
    if (loadingStates.apexDataLoading && loadingStates.plaidDataLoading) {
      return "Loading portfolio and external account data...";
    } else if (loadingStates.apexDataLoading) {
      return "Loading portfolio data...";
    } else if (loadingStates.plaidDataLoading) {
      return "Loading external account data...";
    }
    return "Loading chart data...";
  };

  if (loadingStates.isLoading) {
    return (
      <div className="portfolio-summary-content">
        <div className="performance-section">
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
              width: "100%",
            }}
          >
            <Spin size="large" tip={getLoadingMessage()} />
          </div>
        </div>
      </div>
    );
  }

  if (
    !loadingStates.bothDataSourcesReady ||
    !chartData ||
    chartData.values.length === 0
  ) {
    return (
      <div className="portfolio-summary-content">
        <div className="performance-section">
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
              color: "var(--text-secondary)",
            }}
          >
            {loadingStates.bothDataSourcesReady
              ? loadingStates.hasAnyData
                ? "Chart data is being processed..."
                : "No chart data available from connected accounts"
              : "Preparing chart data..."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="portfolio-summary-content">
      <div className="performance-section">
        <PerformanceTracker
          chartData={chartData}
          timeSelection={timeSelection}
          onTimeSelectionChange={handleTimeSelectionChange}
          onDataUpdate={onDataUpdate}
          hideTimeSelection
        />
      </div>
    </div>
  );
};

export default memo(ChartTab);
